import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styles from 'modules/Tickets/List/tickets-list.module.scss'
import { Button, DatedList, Grid, Icon, Typography } from 'gu-ui-kit'
import EmptyResults from 'components/EmptyResults'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import { useDispatch, useSelector } from 'react-redux'
import config from 'modules/Tickets/List/filters'
import { useTranslation } from 'react-i18next'
import FilterPanel from 'components/Filters/FilterPanel'
import Row from './subcomponents/Row'
import formatRelative from 'utils/dateFns/formatRelative'
import { DROP_LIST, FETCH, PANEL_NAVIGATE, SET_FILTER } from 'modules/Tickets/types'
import { ReactComponent as NoDataFound } from 'images/illustration/no-data-found.svg'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import GridSpinner from 'components/UI/GridSpinner'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { LogoIcon } from 'layouts/subcomponents/Logo'
import debounce from 'lodash/debounce'

const TicketsList = () => {
  const breakpoints = useBreakpoint()
  const dispatch = useDispatch()
  const { t } = useTranslation([])

  const { data, pagination, loaded, loading, loadFailed } = useSelector(({ tickets }) => tickets)

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['tickets'].filters),
    config: config(),
    setAction: SET_FILTER,
    ignoreQuery: true,
    localStorageKey: 'tickets-list-filters',
  })

  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
    dispatch({ type: DROP_LIST })
  }, [filterManager.filters])

  useEffect(() => {
    !!filterManager.params &&
      dispatch({
        type: FETCH,
        payload: {
          params: {
            ...filterManager.params,
            page,
            limit: pagination.limit,
          },
        },
      })
  }, [filterManager.params, page])

  const items = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        date: item?.last_message?.created_at,
      })),
    [data],
  )

  const handleScroll = (e) => {
    if (items.length === 0 || items.length >= pagination.total || loading) {
      return
    }

    if (e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight - 360) {
      setPage((page) => page + 1)
    }
  }

  const debouncedHandleScroll = useCallback(debounce(handleScroll, 250), [items, pagination])

  return (
    <Grid
      direction={'column'}
      className={styles['tickets-list__view']}
    >
      <Grid
        gap={32}
        direction={'column'}
      >
        {breakpoints.sm && (
          <Grid className={styles['tickets-list__header-fixed']}>
            <LogoIcon size={'full'} />
          </Grid>
        )}
        <Grid
          className={styles['tickets-list__header']}
          justifyContent={breakpoints.sm ? 'space-between' : 'flex-start'}
          alignItems={'center'}
          gap={32}
        >
          <Typography
            variant={breakpoints.sm ? 'Header-40' : 'Header-60'}
            component={'h1'}
          >
            {t('tickets::header')}
          </Typography>

          <Button
            onClick={() => {
              dispatch({
                type: PANEL_NAVIGATE,
                payload: { route: 'new_ticket', params: { id: null } },
              })
            }}
            variant={'primary'}
            startIcon={<Icon name={'plus'} />}
            size={'s'}
          >
            {t('tickets::new_ticket')}
          </Button>
        </Grid>

        <FilterPanel
          className={clsx(styles['tickets__filter-panel'], 'margin-bottom-32')}
          filterManager={filterManager}
        />
      </Grid>
      <Grid
        className={styles['tickets-list__wrapper']}
        onScroll={debouncedHandleScroll}
      >
        <DatedList
          showMoreButton={false}
          showDates={false}
          emptyComponent={
            <EmptyResults
              className={styles['tickets-list__empty-results']}
              icon={<NoDataFound />}
              header={t('tickets::empty_list.heading')}
              text={t('tickets::empty_list.label')}
              actionButton={
                <Button
                  size="l"
                  variant="primary"
                  startIcon={<Icon name="plus" />}
                  onClick={() => {
                    dispatch({
                      type: PANEL_NAVIGATE,
                      payload: {
                        route: 'new_ticket',
                        params: { id: null },
                      },
                    })
                  }}
                >
                  {t('tickets::new_ticket')}
                </Button>
              }
            />
          }
          itemComponent={(props) => (
            <Row
              {...props}
              openTicket={(id) =>
                dispatch({
                  type: PANEL_NAVIGATE,
                  payload: { route: 'ticket', params: { id } },
                })
              }
            />
          )}
          pagination={pagination}
          items={items}
          moreButtonLabel={t('common::pagination.show_more')}
          handleGetMoreItems={() => setPage(page + 1)}
          loading={loading}
          loaded={loaded}
          loadFailed={loadFailed}
          l10n={{
            formatFunction: formatRelative,
            today: t('common::dated_list.date_format.today'),
            yesterday: t('common::dated_list.date_format.yesterday'),
            formatDates: {
              short: t('common::dated_list.date_format.short'),
              long: t('common::dated_list.date_format.long'),
            },
          }}
        />
      </Grid>
      {loading && data.length > 0 && (
        <div className={styles['tickets-list__loader']}>
          <GridSpinner width={24} />
        </div>
      )}
    </Grid>
  )
}

export default TicketsList

TicketsList.propTypes = {
  params: PropTypes.object,
}
